import axios from 'axios';
import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { setToken } from '@/Config/auth/authStore';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { getSafeNextPath, OAUTH_NEXT_STORAGE_KEY } from '@/Domain/utils/safeNextPath';

const CONSUME_NONCE_ENDPOINT = '/v1/api/auth/consume_callback_nonce';

function parseHashParams(hash: string): Record<string, string> {
  const params: Record<string, string> = {};
  const stripped = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!stripped) return params;

  const searchParams = new URLSearchParams(stripped);
  searchParams.forEach((value, key) => {
    params[key] = value;
  });
  return params;
}

export function AuthCallbackPage(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = parseHashParams(window.location.hash);
    const scenarioId = params.scenario_id;
    const nonce = params.nonce;

    // Clear the URL fragment BEFORE any async work — keeps the nonce out of
    // browser history, popstate listeners, and analytics URL captures even
    // if the consume call is slow.
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    const bounceToLogin = () => {
      toast.info(t('session_expired'));
      navigate('/', { replace: true });
    };

    if (!nonce) {
      bounceToLogin();
      return;
    }

    // The JWT arrives in the consume response, not the URL fragment — it grows
    // with the user's scenario count and oversized redirect Location headers
    // get rejected by the serving stack. The session-bound single-use nonce
    // still blocks login-CSRF: it only consumes against the session that
    // minted it, so the returned token always matches the browser's session.
    void (async () => {
      let token: string;
      try {
        const response = await axios.post<{ ok: boolean; token: string | null }>(
          CONSUME_NONCE_ENDPOINT,
          { nonce },
          { withCredentials: true },
        );
        if (!response.data.token) {
          bounceToLogin();
          return;
        }
        token = response.data.token;
      } catch {
        bounceToLogin();
        return;
      }

      const stored = setToken(token);
      if (!stored) {
        bounceToLogin();
        return;
      }

      const storedNext = sessionStorage.getItem(OAUTH_NEXT_STORAGE_KEY);
      sessionStorage.removeItem(OAUTH_NEXT_STORAGE_KEY);
      const nextPath = getSafeNextPath(storedNext);
      if (storedNext) {
        // Visibility into the MCP logged-out resume via social login/signup. posthog-js
        // flushes queued events via sendBeacon on the unload window.location.assign triggers.
        posthog.capture(PostHogEventEnum.OauthNextReplayed, {
          next_present: true,
          next_replayed: nextPath !== null,
        });
      }
      if (nextPath) {
        window.location.assign(nextPath);
        return;
      }

      if (scenarioId) {
        navigate(`/i/${scenarioId}/`, { replace: true });
      } else {
        // Brand-new social-auth signup with no ChatImplementation yet.
        // SPA-navigating to `/` would mount BootstrapProvider with no
        // scenario_id and (depending on guards) flash a blank screen.
        // Defer to Django's `index()` view: it knows to redirect a
        // logged-in user with no impl to `/add_new_ai/` and create one.
        window.location.assign('/');
      }
    })();
  }, [navigate, t]);

  return (
    <div
      data-testid="auth-callback-loading"
      className="flex items-center justify-center h-screen w-screen"
    >
      <Spinner className="h-8 w-8 text-muted-foreground" />
    </div>
  );
}
